import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DefaultDataService, HttpUrlGenerator } from '@ngrx/data';
import { Update } from '@ngrx/entity/src/models';
import { TranslateService } from '@ngx-translate/core';
import { WorkflowPresetsApiService } from '@selfai-platform/pipeline-api';
import { AlertService, HistoryBackService, PipelineConfigService } from '@selfai-platform/shared';
import { KE_WORKFLOW_PRESETS_ENTITY_NAME } from '@selfai-platform/store';
import { Observable, tap } from 'rxjs';
import { getPresetsPath } from '../functions';
import { WorkflowPresets } from '../interfaces/workflow-presets.interface';

@Injectable({
  providedIn: 'root',
})
export class WorkflowPresetsService extends DefaultDataService<WorkflowPresets> {
  config = this.pipelineConfigService.getConfig();
  apiUrl = `${this.config.hosts.api}/${this.config.versions.url}/presets`;

  constructor(
    public override http: HttpClient,
    public override httpUrlGenerator: HttpUrlGenerator,
    private router: Router,
    private history: HistoryBackService,
    private pipelineConfigService: PipelineConfigService,
    private alertService: AlertService,
    private translateService: TranslateService,
    private readonly workflowPresetsApiService: WorkflowPresetsApiService,
  ) {
    super(KE_WORKFLOW_PRESETS_ENTITY_NAME, http, httpUrlGenerator);
  }

  override getAll(): Observable<WorkflowPresets[]> {
    return this.workflowPresetsApiService.getAllPresets();
  }

  override getById(id: number): Observable<WorkflowPresets> {
    return this.workflowPresetsApiService.gePresetById(id);
  }

  override add(data: WorkflowPresets): Observable<WorkflowPresets> {
    return this.workflowPresetsApiService.addPreset(data).pipe(
      tap({
        next: () => {
          this.router.navigate(getPresetsPath()).then();
          this.alertService.success(this.translateService.instant('workflow.preset.added'));
        },
        error: () => {
          this.alertService.error(this.translateService.instant('workflow.creation.error'));
        },
      }),
    );
  }

  override update(data: Update<WorkflowPresets>): Observable<WorkflowPresets> {
    return this.workflowPresetsApiService.updatePreset(data.changes).pipe(
      tap({
        next: () => {
          this.history.back(getPresetsPath());
          this.alertService.success(this.translateService.instant('workflow.preset.changed'));
        },
        error: () => {
          this.alertService.error(this.translateService.instant('workflow.editing.error'));
        },
      }),
    );
  }

  override delete(id: string): Observable<string> {
    return this.workflowPresetsApiService.deletePreset(id).pipe(
      tap({
        next: () => {
          this.alertService.success(this.translateService.instant('workflow.successful.removal'));
        },
        error: () => {
          this.alertService.error(this.translateService.instant('workflow.uninstall.error'));
        },
      }),
    );
  }
}
